import React, { useEffect } from 'react';
import { Box, Button, Card, CardContent, Chip, Typography } from '@mui/material';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { getInstagramAutomationStatus } from '../../redux/socialPosts/action';
import { selectInstagramCommentAutomationGloballyEnabled } from '../../redux/socialPosts/selector';
import { IG_AUTO_GOLD, IG_AUTO_MINT } from './instagramAutomationTheme';
import { usePlanEntitlementGates } from '../../hook/usePlanEntitlementGates';

export interface WorkshopInstagramAutomationCardProps {
    onOpenModal: () => void;
    hasMedia: boolean;
    automationActive?: boolean;
}

export function WorkshopInstagramAutomationCard({
    onOpenModal,
    hasMedia,
    automationActive,
}: WorkshopInstagramAutomationCardProps) {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const instagramCommentAutomationEnabled = useSelector(selectInstagramCommentAutomationGloballyEnabled);
    const { canDirectUpload, hasInstagramAutomation, requireUploadEntitlement } = usePlanEntitlementGates();

    useEffect(() => {
        if (instagramCommentAutomationEnabled) {
            dispatch(getInstagramAutomationStatus({}));
        }
    }, [dispatch, instagramCommentAutomationEnabled]);

    const handleOpen = () => {
        if (!requireUploadEntitlement()) return;
        onOpenModal();
    };

    return (
        <Card sx={{ mb: 2, bgcolor: theme[themeStyle].deep_grey_lighter, p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AutoAwesomeOutlinedIcon sx={{ color: IG_AUTO_GOLD, fontSize: 18 }} />
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 15,
                        color: theme[themeStyle].tag_white,
                        flex: 1,
                    }}>
                    Post Automation
                </Typography>
                {automationActive && (
                    <Chip
                        label="IG live"
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: 10,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            bgcolor: 'rgba(0,253,193,0.12)',
                            color: IG_AUTO_MINT,
                        }}
                    />
                )}
            </Box>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 12,
                        color: '#fff',
                        lineHeight: 1.4,
                        mb: 1.25,
                    }}>
                    Post to Instagram, TikTok, and YouTube from one caption.
                    {instagramCommentAutomationEnabled && hasInstagramAutomation
                        ? ' Optional comment-to-DM automation on Instagram.'
                        : instagramCommentAutomationEnabled
                        ? ' Upgrade to Pro for Instagram comment-to-DM automation.'
                        : ''}
                </Typography>
                {!hasMedia && (
                    <Typography sx={{ fontSize: 11, color: theme[themeStyle].primary_orange, mb: 1 }}>
                        Finish uploading your video to enable social posting.
                    </Typography>
                )}
                {!canDirectUpload && hasMedia && (
                    <Typography sx={{ fontSize: 11, color: '#fff', mb: 1 }}>
                        Pro unlocks direct social upload and Instagram automation.
                    </Typography>
                )}
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleOpen}
                    disabled={!hasMedia}
                    sx={{
                        borderColor: 'rgba(255, 201, 101, 0.35)',
                        color: IG_AUTO_GOLD,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'none',
                        borderRadius: 2,
                        py: 1,
                        '&:hover': {
                            borderColor: IG_AUTO_GOLD,
                            bgcolor: 'rgba(255, 201, 101, 0.08)',
                        },
                    }}>
                    {!canDirectUpload
                        ? 'Upgrade to upload'
                        : automationActive
                        ? 'Manage posts'
                        : 'Open post automation'}
                </Button>
            </CardContent>
        </Card>
    );
}
